/**
 * Clinic Location Page
 *
 * 拠点管理 - マスタメンテナンス
 * Display and search clinic locations (list view only).
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import type { TableProps } from 'antd';
import { useMainClinicLocations } from '@/hooks/main/useMainClinicLocation';
import { useGlobalBehavior } from '@/shared/providers/GlobalBehaviorWrapper';
import type { ClinicLocation, ClinicLocationSearchParams } from './types';
import {
  AppTable,
  AppTextField,
  AppButton,
  AppTableActionButton,
  AppPagination,
  AppSpin,
  AppForm,
  AppRow,
  AppCol,
  AppFormLabel,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms/RA2NDatePicker';
import { CollapsibleCard } from '@/components/molecules';
import { useRedirectWhenPageEmpty } from '@/hooks/useRedirectWhenPageEmpty';
import { formatPeriodDisplay } from '@/utils/dateConverter';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';
import { convertSeirekiWrap } from '@/utils/wareki';
import { DEFAULT_PER_PAGE } from '@/constants';

/**
 * Main ClinicLocationPage Component
 */
const ClinicLocationPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSite = tenant ?? 'medix';
  const { setEnterKeyNavigationEnabled } = useGlobalBehavior();

  // Disable Enter key navigation on this page (used for search inputs)
  useEffect(() => {
    setEnterKeyNavigationEnabled(false);
    return () => setEnterKeyNavigationEnabled(true);
  }, [setEnterKeyNavigationEnabled]);

  // Form state (for input fields - converted for storage)
  const [searchName, setSearchName] = useState(searchParams.get('location_name') || '');
  const [searchDate, setSearchDate] = useState(searchParams.get('available_date') || '');

  // Query params state (trigger API when changed)
  const queryParams = useMemo<ClinicLocationSearchParams>(() => {
    const rawDate = searchParams.get('available_date');
    let apiDate: string | undefined = undefined;

    if (rawDate) {
      const seireki = convertSeirekiWrap(rawDate);
      const d = dayjs(seireki.replace(/\//g, '-'));
      if (d.isValid()) {
        apiDate = d.format('YYYY-MM-DD');
      } else {
        apiDate = 'invalid';
      }
    }

    return {
      per_page: DEFAULT_PER_PAGE,
      page: Number(searchParams.get('page')) || 1,
      location_name: searchParams.get('location_name') || undefined,
      available_date: apiDate,
    };
  }, [searchParams]);

  // React Query hooks
  // Disable query if date is invalid to avoid unnecessary API calls that might error
  const {
    data: listResponse,
    isFetching: isLoading,
    isError: listError,
  } = useMainClinicLocations(queryParams, { enabled: queryParams.available_date !== 'invalid' });

  // Extract data from responses
  const isInvalidDate = queryParams.available_date === 'invalid';
  const locations = isInvalidDate ? [] : listResponse?.data?.data || [];
  const pagination = isInvalidDate ? null : listResponse?.data?.pagination || null;
  const total = isInvalidDate ? 0 : pagination?.total || 0;

  // When URL has page > 1 but API returns no data (e.g. last item on page deleted), go to page 1
  useRedirectWhenPageEmpty({
    searchParams,
    setSearchParams,
    isLoading,
    isError: listError,
    dataLength: locations.length,
  });

  // Handle detail navigation
  const handleDetailClick = (locationId: number) => {
    navigate(`/${currentSite}/clinic_locations/${locationId}`);
  };

  // Table columns definition
  const tableColumns: TableProps<ClinicLocation>['columns'] = useMemo(
    () => [
      {
        title: '拠点番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 80,
        align: 'center',
      },
      {
        title: '拠点名',
        dataIndex: 'location_name',
        key: 'location_name',
        width: 360,
        ellipsis: false,
        render: (_, record) => renderTableCellTruncate(record.location_name ?? '', { width: 360 }),
      },
      {
        title: '住所',
        key: 'address',
        width: 360,
        ellipsis: false,
        render: (_, record) =>
          renderTableCellTruncate(`${record.address1 || ''}${record.address2 || ''}`, {
            width: 360,
          }),
      },
      {
        title: '有効期限',
        key: 'available_period',
        render: (_, record) => formatPeriodDisplay(record.available_from, record.available_to),
      },
      {
        title: '操作',
        key: 'action',
        align: 'center',
        className: '!w-0 whitespace-nowrap',
        width: 60,
        render: (_, record) => (
          <div className="flex justify-center gap-1 px-2 w-fit">
            <AppTableActionButton
              action="detail"
              onClick={() => handleDetailClick(record.id)}
              title="詳細表示"
            >
              詳細
            </AppTableActionButton>
          </div>
        ),
      },
    ],
    []
  );

  // Handle search button click
  const handleSearch = () => {
    setSearchParams({
      page: '1',
      location_name: searchName || '',
      available_date: searchDate || '',
    });
  };

  const handlePageChange = (page: number) => {
    if (!isLoading) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', page.toString());
      setSearchParams(newParams);
    }
  };

  return (
    <AppSpin spinning={isLoading}>
      <div className="pb-4 space-y-4">
        {/* Header */}
        <CollapsibleCard title="拠点一覧">
          {/* Search Section */}
          <AppForm layout="vertical" className="w-full">
            <AppRow gutter={[16, 12]} align="bottom">
              <AppCol xs={24} sm={12} lg={8} xl={7}>
                <div className="space-y-1">
                  <AppFormLabel label="拠点名" />
                  <AppTextField
                    value={searchName}
                    onChange={e => setSearchName(e.target.value)}
                    data-skip-enter-nav
                    onPressEnter={handleSearch}
                    className="w-full"
                    placeholder="入力してください"
                  />
                </div>
              </AppCol>
              <AppCol xs={24} sm={12} lg={6} xl={5}>
                <div className="space-y-1">
                  <AppFormLabel label="基準日" />
                  <RA2NDatePicker
                    value={searchDate || null}
                    onEnter={handleSearch}
                    placeholder="元号00年00月00日"
                    onChange={(val: string) => setSearchDate(val)}
                    className="w-full"
                  />
                </div>
              </AppCol>
              <AppCol xs={24} sm={12} lg={4} xl={3}>
                <AppButton
                  onClick={handleSearch}
                  type="primary"
                  icon={<i className="fa fa-search" />}
                  className="w-full sm:w-auto"
                >
                  検索
                </AppButton>
              </AppCol>
            </AppRow>
          </AppForm>
        </CollapsibleCard>
        <CollapsibleCard>
          <div className="mb-4">
            <div className="text-sm font-medium text-gray-700">該当件数: {total} 件</div>
          </div>
          <AppTable
            columns={tableColumns}
            dataSource={locations}
            rowKey="id"
            pagination={false}
            tableLayout="auto"
            loading={false}
          />

          {/* Pagination */}
          <div className="flex justify-center mt-4">
            <AppPagination
              current={pagination?.current_page}
              total={pagination?.total}
              pageSize={pagination?.per_page}
              onChange={handlePageChange}
              hideOnSinglePage
            />
          </div>
        </CollapsibleCard>
      </div>
    </AppSpin>
  );
};

export default ClinicLocationPage;
